import { BoardTemplate } from '../mainApi';
import { ApiClientService } from './ApiClientService';

type TemplatesResponse = {
  templates: BoardTemplate[];
};

type TemplateResponse = {
  template: BoardTemplate;
};

export type TemplateInstanceInput = {
  name: string;
  workspace: string;
};

export type TemplateUpdateInput = {
  name: string;
  description: string;
};

export type TemplateShareInput = {
  emailAddresses: string;
  message: string;
};

export type TemplateShareDetails = {
  status: 'pending' | 'accepted';
  senderName: string;
  recipientEmail: string;
  template: BoardTemplate;
  copiedTemplateId?: string | null;
};

type TemplateSharesResponse = {
  invited: string[];
  alreadyAccepted: string[];
};

type TemplateShareResponse = {
  share: TemplateShareDetails;
};

type TemplateInstanceResponse = {
  path?: string;
  topic?: {
    id?: string;
    _id?: string;
  };
  board?: {
    id?: string;
    _id?: string;
  };
  game?: {
    id?: string;
    _id?: string;
  };
};

export const getTemplates = async () => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  const response = await ApiClientService.getJson<TemplatesResponse>(`${apiRoot}/templates`);

  return response.templates ?? [];
};

export const getMarketplaceTemplates = async () => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  const response = await ApiClientService.getJson<TemplatesResponse>(
    `${apiRoot}/templates/marketplace`
  );

  return response.templates ?? [];
};

export const deleteTemplate = async (templateId: string) => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  await ApiClientService.deleteJson<void>(`${apiRoot}/templates/${templateId}`);
};

export const updateTemplate = async (templateId: string, input: TemplateUpdateInput) => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  const response = await ApiClientService.putJson<TemplateResponse>(
    `${apiRoot}/templates/${templateId}`,
    input
  );

  return response.template;
};

export const shareTemplate = async (templateId: string, input: TemplateShareInput) => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  return ApiClientService.postJson<TemplateSharesResponse>(
    `${apiRoot}/templates/${templateId}/shares`,
    input
  );
};

export const getTemplateShare = async (token: string) => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  const response = await ApiClientService.getJson<TemplateShareResponse>(
    `${apiRoot}/template-shares/${encodeURIComponent(token)}`
  );

  return response.share;
};

export const acceptTemplateShare = async (token: string) => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  const response = await ApiClientService.postJson<TemplateResponse>(
    `${apiRoot}/template-shares/${encodeURIComponent(token)}/accept`,
    {}
  );

  return response.template;
};

export const copyMarketplaceTemplate = async (templateId: string) => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  const response = await ApiClientService.postJson<TemplateResponse>(
    `${apiRoot}/templates/marketplace/${templateId}/copy`,
    {}
  );

  return response.template;
};

export const createTemplateInstance = async (
  templateId: string,
  input: TemplateInstanceInput,
  source: 'user' | 'marketplace' = 'user'
) => {
  const apiRoot = ApiClientService.getApiBaseUrl();
  const endpoint =
    source === 'marketplace'
      ? `${apiRoot}/templates/marketplace/${templateId}/instances`
      : `${apiRoot}/templates/${templateId}/instances`;
  const response = await ApiClientService.postJson<TemplateInstanceResponse>(endpoint, input);
  const topicId = response.topic?.id ?? response.topic?._id ?? '';
  const boardId = response.board?.id ?? response.board?._id ?? '';
  const gameId = response.game?.id ?? response.game?._id ?? '';

  if (response.path) {
    return response.path;
  }

  if (topicId) {
    return `/b/${topicId}`;
  }

  if (boardId) {
    return `/w/${boardId}`;
  }

  return gameId ? `/g/${gameId}/cards` : null;
};
